/**
 * useOnboarding — server-backed gate for the guided coach-mark tours.
 *
 * The per-view OnboardingModal answers "what is this view" and tracks
 * dismissal in localStorage. The coach-mark tours answer "what do I DO
 * here" and track completion server-side (on the user record) so a tour a
 * canvasser finished on a loaner phone doesn't re-appear on their own — see
 * the `onboarding_state` column + PATCH /auth/me/preferences.
 *
 * Usage:
 *   const { hasSeen, markSeen } = useOnboarding();
 *   if (!hasSeen('canvasser-home-v1')) startTour();
 *   // on finish/skip:
 *   await markSeen('canvasser-home-v1');
 *
 * `markSeen` is idempotent, optimistic-safe (it reads the freshest server
 * `seen` list before appending so two tours completed in one session don't
 * clobber each other), and fire-and-forget friendly — it refreshes the auth
 * user afterward so `hasSeen` reflects the new state without a reload.
 */
import { useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { patchUserPreferences } from '@/api/settings';

export function useOnboarding() {
  const { user, refreshUser } = useAuth();

  const seen = user?.onboarding_state?.seen ?? [];

  const hasSeen = useCallback(
    (tourId: string): boolean => seen.includes(tourId),
    [seen],
  );

  const markSeen = useCallback(
    async (tourId: string): Promise<void> => {
      // Read the freshest list off the current user rather than closing over
      // a stale `seen` — cheap insurance against clobbering a concurrently
      // completed tour.
      const current = user?.onboarding_state?.seen ?? [];
      if (current.includes(tourId)) return;
      const next = [...current, tourId];
      try {
        await patchUserPreferences({ onboarding_state: { seen: next } });
        // Pull the canonical user back so `hasSeen` updates app-wide.
        await refreshUser();
      } catch {
        // Non-fatal: a failed PATCH just means the tour may show again next
        // session. Better that than a hard error blocking the field UI.
      }
    },
    [user, refreshUser],
  );

  return { hasSeen, markSeen, seen };
}

export default useOnboarding;
